import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../constant/constant";
import { useNavigate } from "react-router-dom";

/** ====== CONFIG (adjust as needed) ====== */
const SESSION_MS = 15 * 60 * 1000;        // 15 minutes
const KEEPALIVE_INTERVAL_MS = 1 * 60 * 1000; // 1 min
const TICK_MS = 1000;                        // update every second
const STORAGE_KEYS = {
  token: "token",
  expiryTime: "expiryTime",  
  lastActivity: "lastActivity", 
};
/** ====================================== */

const API = axios.create({ baseURL: API_BASE_URL });

function attachInterceptors(onUnauthorized, onActivity) {
  API.interceptors.request.use((config) => {
    const token = localStorage.getItem(STORAGE_KEYS.token);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  API.interceptors.response.use(
    (res) => {
      onActivity?.("axios-response");
      return res;
    },
    (err) => {
      if (err?.response?.status === 401) {
        onUnauthorized?.();
      }
      return Promise.reject(err);
    }
  );
}

export default function SessionManager() {
  const [remaining, setRemaining] = useState(null);

  const tickIntervalRef = useRef(null);
  const keepAliveTimeoutRef = useRef(null);
  const idleTimeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const expiryAtRef = useRef(null);

  // --- Helpers -------------------------------------------------------------

  const getStoredExpiry = () => {
    const iso = localStorage.getItem(STORAGE_KEYS.expiryTime);
    return iso ? new Date(iso).getTime() : null;
  };




  
  const setStoredExpiry = (msEpoch) => {
    localStorage.setItem(STORAGE_KEYS.expiryTime, new Date(msEpoch).toISOString());
  };

  
const bumpActivity = async (reason = "event") => {
  lastActivityRef.current = Date.now();
  localStorage.setItem(STORAGE_KEYS.lastActivity, String(lastActivityRef.current));

  // Optional: if near expiry, trigger a refresh
  const remainingMs = (expiryAtRef.current ?? 0) - Date.now();
  if (remainingMs < 12 * 60 * 1000) { // less than 12 min
    try {
      const token = localStorage.getItem(STORAGE_KEYS.token);
//console.log("Tokane Value :" ,token)


      if (token) {
        const res = await axios.post(
          `${API_BASE_URL}/session/refresh`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
                  );
        if (res?.data?.expiryTime) {
          const serverExpiry = new Date(res.data.expiryTime).getTime();
          expiryAtRef.current = serverExpiry;
          setStoredExpiry(serverExpiry);
          scheduleTimers(); // 🔑 re-sync timers
        }
      }
    } catch (e) {
      console.error("Failed to refresh on activity:", e);
    }
  }
};



const navigate = useNavigate();




  const clearTimers = () => {
    if (tickIntervalRef.current) clearInterval(tickIntervalRef.current);
    if (keepAliveTimeoutRef.current) clearTimeout(keepAliveTimeoutRef.current);
    if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current);
  };

    const handleLogout = async () => {
  
    try {
      const token = localStorage.getItem("token");
      if (token) {
        await axios.post(`${API_BASE_URL}/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
    } catch (_) {
      // ignore network errors on logout
    } finally {
      navigate("/", { replace: true });
          localStorage.setItem('isAuthenticated', 'false');
          localStorage.clear();
          sessionStorage.clear();
    }
  };
/** ---- NEW: keepalive forcely every 5 min ---- */
/** ---- FORCE keepalive every 5 minutes ---- */
const runKeepAlive = async () => {
  try {
    const token = localStorage.getItem(STORAGE_KEYS.token);
    if (!token) return;

    const res = await axios.post(
      `${API_BASE_URL}/session/refresh`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );

   // console.log("Keepalive forced refresh:", res.data);

    if (res?.data?.expiryTime) {
      const serverExpiry = new Date(res.data.expiryTime).getTime();
      expiryAtRef.current = serverExpiry;     // ✅ sync ref
      setStoredExpiry(serverExpiry);          // ✅ sync localStorage
    } else {
      // fallback if server didn’t send expiryTime
      const fallback = Date.now() + 5 * 60 * 1000;
      expiryAtRef.current = fallback;
      setStoredExpiry(fallback);
    }
  } catch (e) {
    console.error("Keepalive failed:", e);

    // fallback: still extend locally by 5 min
    const fallback = Date.now() + 5 * 60 * 1000;
    expiryAtRef.current = fallback;
    setStoredExpiry(fallback);
  } finally {
    // schedule next keepalive after 5 min
    keepAliveTimeoutRef.current = setTimeout(runKeepAlive, KEEPALIVE_INTERVAL_MS);
  }
};


  const scheduleTimers = () => {
    clearTimers();

    // TICK: update countdown
    tickIntervalRef.current = setInterval(() => {
      const now = Date.now();
      const rem = Math.max(0, (expiryAtRef.current ?? now) - now);
      setRemaining(rem);
      if (rem === 0) {
        handleLogout();
      }
    }, TICK_MS);

    // KEEPALIVE check every 2 min
    keepAliveTimeoutRef.current = setTimeout(runKeepAlive, KEEPALIVE_INTERVAL_MS);

    // ABSOLUTE cutoff
    const msUntilExpiry = Math.max(0, (expiryAtRef.current ?? Date.now()) - Date.now());
    idleTimeoutRef.current = setTimeout(handleLogout, msUntilExpiry + 250);
  };

  // --- Effects -------------------------------------------------------------

  useEffect(() => {
    const storedLast = Number(localStorage.getItem(STORAGE_KEYS.lastActivity) || 0);
    lastActivityRef.current = storedLast > 0 ? storedLast : Date.now();

    const storedExpiry = getStoredExpiry();
    if (storedExpiry && storedExpiry > Date.now()) {
      expiryAtRef.current = storedExpiry;
    } else {
      expiryAtRef.current = lastActivityRef.current + SESSION_MS;
      if (expiryAtRef.current <= Date.now()) {
        handleLogout();
        return;
      }
      setStoredExpiry(expiryAtRef.current);
    }

    attachInterceptors(handleLogout, bumpActivity);
    scheduleTimers();

    const activityHandler = () => {
      if ((expiryAtRef.current ?? 0) > Date.now()) bumpActivity("dom-event");
    };
    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    events.forEach((ev) => window.addEventListener(ev, activityHandler, { passive: true }));

    const onVis = () => {
      if (document.visibilityState === "visible") {
        const fresh = getStoredExpiry();
        if (fresh) expiryAtRef.current = fresh;
        if ((expiryAtRef.current ?? 0) <= Date.now()) {
          handleLogout();
        } else {
          scheduleTimers();
        }
      }
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      clearTimers();
      events.forEach((ev) => window.removeEventListener(ev, activityHandler));
      document.removeEventListener("visibilitychange", onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- UI ------------------------------------------------------------------

  const formatTime = (ms) => {
    const total = Math.floor(ms / 1000);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    return h > 0
      ? `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
      : `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  if (remaining === null) return null;

  return (
    <div
      style={{
        fontWeight: 700,
        fontSize: 14,
        padding: "6px 10px",
        borderRadius: 8,
        background: remaining < 60_000 ? "#ffebee" : "#e8f5e9",
        color: remaining < 60_000 ? "#c62828" : "#2e7d32",
      }}
      title="Auto-logout on inactivity"
    >
      Software Close in: {formatTime(remaining)} Minutes
    </div>
  );
}
